import React, { useRef, useEffect, useState } from "react";
import { useStateValue } from './StateProvider';

import './SystemDiagram.css';

const assetTypes = [
    { id: "electrical", name: "Electrical" },
    { id: "water", name: "Water" },
];

const filterMap = {
    electrical: ["generator", "circuitbreaker", "switch"],
    mep: ["pumps", "tanks"],
    water: ["tank", "pump"]
};

const Generator = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle r="20" stroke="black" fill="white" />
        <text x="-8" y="5" fontSize="12">G</text>
        <text
            x="0"
            y="35"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>
    </g>
);

const Pump = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle r="20" stroke="black" fill="white" />
        <polygon points="-10,0 10,-10 10,10" fill="black" />
        <text
            x="0"
            y="35"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>
    </g>
);

const Tank = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <rect x="-20" y="-30" width="40" height="60" stroke="black" fill="lightblue" />
        <text
            x="0"
            y="45"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>

    </g>
);

const Valve = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle r="15" stroke="black" fill="white" />
        <line x1="-15" y1="0" x2="15" y2="0" stroke="black" />
        <text
            x="0"
            y="35"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>
    </g>
);

const HVAC = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle r="20" stroke="black" fill="lightgray" />
        <line x1="-15" y1="-15" x2="15" y2="15" stroke="black" />
        <line x1="-15" y1="15" x2="15" y2="-15" stroke="black" />
        <text
            x="0"
            y="35"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>
    </g>
);

const Switch = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle cx="-15" cy="0" r="2" fill="black" />
        <circle cx="15" cy="0" r="2" fill="black" />
        <line x1="-15" y1="0" x2="10" y2="-10" stroke="black" strokeWidth="2" />
        <circle cx="-15" cy="0" r="1" fill="black" />
        <text
            x="0"
            y="35"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>
    </g>
);


const Line = ({ x1, y1, x2, y2, stroke = "black", strokeWidth = 2 }) => (
    <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={stroke}
        strokeWidth={strokeWidth}
        markerEnd="url(#arrow)"
    />
);

const Default = ({ name, x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
        <circle r="20" stroke="black" fill="white" />
        <text
            x="0"
            y="35"
            fontSize="10"
            textAnchor="middle"
        >
            {name}
        </text>
    </g>
);

function SystemDiagram({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [filter, setFilter] = useState(null);
    const [filteredassets, setFilteredassets] = useState([]);
    const [connections, setConnections] = useState([]);

    const svgRef = useRef(null);
    const [viewBox, setViewBox] = useState("-50 -50 800 600");

    useEffect(() => {
        let filtered;

        const assetsSet = new Set();

        project.connections.forEach(group => {
            group.rows.forEach(row => {
                assetsSet.add(row.fromassetid);
                assetsSet.add(row.toassetid);
            });
        });
        filtered = Array.from(assetsSet).map(id => project.assets.find(a => a.id === id))

        if (filter) {
            const acceptedTypes = filterMap[filter] || [filter];
            filtered = filtered.filter(a => acceptedTypes.includes(a.type));
        }

        const spacingX = 120;
        const spacingY = 100;
        const perRow = 5;

        filtered.forEach((asset, index) => {
            const col = index % perRow;
            const row = Math.floor(index / perRow);

            asset.cx = col * spacingX;
            asset.cy = row * spacingY;
        });

        let conn = [];

        project.connections.forEach(group => {
            group.rows.forEach(row => {
                const from = filtered.find(a => a.id === row.fromassetid);
                const to = filtered.find(a => a.id === row.toassetid);

                if (!from || !to) return;

                conn.push({
                    x1: from.cx,
                    y1: from.cy,
                    x2: to.cx,
                    y2: to.cy
                });
            });
        });

        setFilteredassets(filtered);
        setConnections(conn)
    }, [filter, project.assets, project.connections]);

    function Asset({ asset }) {
        let type = asset.type;

        switch (type) {
            case "generator":
                return <Generator name={asset.name} x={asset.cx} y={asset.cy} />;
            case "pump":
                return <Pump name={asset.name} x={asset.cx} y={asset.cy} />
            case "tank":
                return <Tank name={asset.name} x={asset.cx} y={asset.cy} />
            case "valve":
                return <Valve name={asset.name} x={asset.cx} y={asset.cy} />
            case "hvac":
                return <HVAC name={asset.name} x={asset.cx} y={asset.cy} />
            case "switch":
                return <Switch name={asset.name} x={asset.cx} y={asset.cy} />
            default:
                return <Default name={asset.name} x={asset.cx} y={asset.cy} />
        }
    }

    return (
        <div className="systemdiagram">
            <div className='systemdiagram_filters'>
                <button
                    onClick={() => setFilter(null)}
                    className='systemdiagram_filterbutton'
                >
                    All
                </button>

                {
                    assetTypes.map((type, index) => (
                        <button
                            key={'type' + index}
                            onClick={() => setFilter(type.id)}
                            className='systemdiagram_filterbutton'
                        >
                            {type.name}
                        </button>
                    ))
                }
            </div>

            <button
                className="button"
                onClick={() => {
                    const svgElement = svgRef.current;
                    const serializer = new XMLSerializer();
                    const source = serializer.serializeToString(svgElement);
                    const blob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "System_diagram.svg";
                    a.click();
                }}
            >
                Export SVG
            </button>

            <svg
                ref={svgRef}
                viewBox={viewBox}
                width="100%"
                height="600"
                style={{ border: "1px solid #ccc", background: "#fdfdfd" }}
            >
                <defs>
                    <marker
                        id="arrow"
                        markerWidth="10"
                        markerHeight="10"
                        refX="10"
                        refY="3"
                        orient="auto"
                        markerUnits="strokeWidth"
                    >
                        <path d="M0,0 L0,6 L9,3 z" fill="#333" />
                    </marker>
                </defs>

                {
                    filteredassets.map((asset) => {
                        return <Asset
                            key={asset.id}
                            asset={asset}
                        />
                    })
                }

                {connections.map((c, i) => (
                    <Line key={i} {...c} />
                ))}
            </svg>
        </div>
    );
}

export default SystemDiagram;